import 'firebase/compat/analytics'
import 'firebase/compat/app-check'
import 'firebase/compat/performance'
import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import { createApp, defineCustomElement } from 'vue'
import * as Sentry from '@sentry/vue'
import firebase from 'firebase/compat/app'
import number from '@coders-tm/vue-number-format'
import Popper from 'vue3-popper'
import VueDatePicker from '@vuepic/vue-datepicker'
import VueVirtualScroller from 'vue-virtual-scroller'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'

import '@vuepic/vue-datepicker/dist/main.css'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
// vue-tel-input CSS moved to PhoneInput.vue
import './index.css'
import 'floating-vue/dist/style.css'

import router from './router'
import store from './store'

import App from './App.vue'
import OMNode from './customElements/OMNode.ce.vue'
import OrgNode from './customElements/OrgNode.ce.vue'
import FloatingVue from 'floating-vue'
import { get as getDoman } from 'psl'

// Instrumentation
import { faro } from './faro_instrument.js'
import { logger } from '@/utils/logger'
import { initializeNetworkTelemetry, initializeTelemetryDownloadShortcut } from '@/utils/telemetry'
initializeNetworkTelemetry()
initializeTelemetryDownloadShortcut()
logger.info('Faro instrumentation:', faro ? 'enabled' : 'disabled')

/**
 * @description Returns the environment for firebase.
 *  If in "agentnoon" env, then rely on `import.meta.env.MODE`.
 *  If in "Dayforce", then rely on the hostname. The reason is because Dayforce
 *  uses one container build and pushes it to all environments. We currently do
 *  not have the option to build for specific environments.
 *
 *  @returns {("ajz"|"staging"|"production")}
 */
function getEnv() {
  const domain = getDoman(window.location.hostname)
  if (domain === 'dayforcehcm.com' || domain === 'dayforce.com') {
    // We are in dayforce. Rely on the hostname purely to determine the environment for firebase purposes.
    const env = window.location.hostname.split('.')[1]
    if (env === 'np' || env === 'pre') {
      // we are in non-prod or pre-prod. Use staging
      return 'staging'
    } else {
      // else, use production
      return 'production'
    }
  }
  // Checking if we are in aljazeera
  if (domain === 'aljazeera.net') return 'ajz'
  return import.meta.env.MODE
}

const app = createApp(App)
const pinia = createPinia()
const env = getEnv()
if (env === 'ajz') {
  // Production environment
  const firebaseConfig = {
    apiKey: 'AIzaSyC7Q6S7f2b1uHI8AooGHQONKxUIJRiHBu4',
    authDomain: 'ajgc-apw-app-prod-agentnoon-01.firebaseapp.com',
    projectId: 'ajgc-apw-app-prod-agentnoon-01',
    storageBucket: 'ajgc-apw-app-prod-agentnoon-01.firebasestorage.app',
    messagingSenderId: '523410560181',
    appId: '1:523410560181:web:85581e3a162edd4c5b5afc'
  }

  // Initialize Firebase
  firebase.initializeApp(firebaseConfig)
  logger.info('connected to firebase ajz-production')
} else if (env === 'production') {
  // Production environment
  const firebaseConfig = {
    apiKey: 'AIzaSyCbJ-27TQG4TMws5BxSf99N4wpnMbXaihc',
    authDomain: 'auth.agentnoon.com',
    databaseURL: 'https://orgraph-d57a6.firebaseio.com',
    projectId: 'orgraph-d57a6',
    storageBucket: 'orgraph-d57a6.appspot.com',
    messagingSenderId: '513813238878',
    appId: '1:513813238878:web:2068fa1d9fdd0e152810cf',
    measurementId: 'G-M959PKHY2G'
  }

  // Initialize Firebase
  firebase.initializeApp(firebaseConfig)
  logger.info('connected to firebase production')
} else {
  const firebaseConfig = {
    apiKey: 'AIzaSyDq_MtrRO19v5SjaxPzt2D1E0oGtHDR9VA',
    authDomain: 'staging.auth.agentnoon.com',
    projectId: 'agentnoon-staging',
    storageBucket: 'agentnoon-staging.appspot.com',
    messagingSenderId: '706148019191',
    appId: '1:706148019191:web:3a3a0d023b2b352a089cf2'
  }
  // Initialize Firebase
  firebase.initializeApp(firebaseConfig)
  logger.info('connected to firebase staging')
}

if (env != 'production') {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN
}
const appCheckHosts = ['app.agentnoon.com']
if (appCheckHosts.includes(window.location.hostname)) {
  // Create a ReCaptchaEnterpriseProvider instance using your reCAPTCHA Enterprise
  // site key and pass it to activate().
  const appCheck = firebase.appCheck()
  appCheck.activate(
    new firebase.appCheck.ReCaptchaEnterpriseProvider('6LdyQMorAAAAANPAonEhfxR09AAPn9GUrjGFq9sv'),
    true // Set to true to allow auto-refresh.
  )
}

Sentry.init({
  app,
  enabled: window.location.hostname.includes('app.agentnoon.com'),
  dsn: 'https://b8bbaeb9f316499aad2b598a051caa32@o970831.ingest.sentry.io/6123697',
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true
    })
  ],
  tracePropagationTargets: [/https:\/\/api(-aus|-eu|-doha|-singapore)?\.agentnoon\.co\/v1/],
  environment: env,

  // https://docs.sentry.io/platforms/javascript/configuration/sampling/#setting-a-sampling-function
  // https://docs.sentry.io/platforms/javascript/tracing/configure-sampling/#traces-sampler-examples
  tracesSampler: ({ name, inheritOrSampleWith }) => {
    const lowercaseName = name.toLowerCase()

    // High-traffic pages
    const publicPages = ['home', 'landing']
    if (publicPages.some((page) => lowercaseName.includes(page))) {
      return 0.1
    }

    // Critical business flows
    const criticalFlows = ['MainOrgImportCSV', 'MainOrgImportData', 'Hub']
    if (criticalFlows.some((flow) => lowercaseName.includes(flow))) {
      return 0.8
    }

    // Default sampling rate for everything else
    return inheritOrSampleWith(0.5)
  },
  logErrors: true
})

app.use(router)
app.use(store)
app.use(autoAnimatePlugin)
app.use(number, { precision: 2 })
app.use(VueVirtualScroller)
app.use(FloatingVue)
app.use(VueQueryPlugin)
app.use(pinia)

app.component('PopperTooltip', Popper)
app.component('VueDatePicker', VueDatePicker)

app.mount('#app')

// When an error occurs in Vue app, the native error output does not include
// the line number. This allows it.
app.config.errorHandler = (error) => {
  // eslint-disable-next-line no-console -- Vue errorHandler must use console before logger mounts
  console.error(error)
}

const anOrgNode = defineCustomElement(OrgNode)
const anOMNode = defineCustomElement(OMNode)

customElements.define('an-org-node', anOrgNode)
customElements.define('an-om-node', anOMNode)
